// src/line/hooks/useLineAuth.js
import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { useLocation } from "react-router-dom";
import { app, auth } from "../../firebase/firebase";

const D = {
  dbg: (...a) => { try { window.__LINE_DBG_PUSH?.({ type: "log", msg: a.join(" ") }); } catch {} },
  dbgError: (...a) => { try { window.__LINE_DBG_PUSH?.({ type: "error", error: a.join(" ") }); } catch {} },
  maybeEnableFromQuery: () => {},
  maybeEnableFromState: () => {},
  hookGlobalErrors: () => {},
  dbgEnabled: () => false,
};

const SS_KEY_PROFILE = "line_profile";
const SS_KEY_STATE = "line_oauth_state";

export default function useLineAuth() {
  const routerLocation = useLocation(); // ← “location”グローバルと区別
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY_PROFILE) || "null"); } catch { return null; }
  });
  const [lineUserId, setLineUserId] = useState(profile?.userId || null);
  const [error, setError] = useState(null);

  const redirectUri = `${window.location.origin}${window.location.pathname}`;

  useEffect(() => {
    (async () => {
      // 1) デバッグユーティリティを動的ロード（失敗してもダミーで継続）
      try {
        const mod = await import("../utils/lineDebug");
        Object.assign(D, mod);
      } catch {}

      // 2) デバッグ有効化＆グローバルエラーフック（有効時のみ実動）
      D.maybeEnableFromQuery(routerLocation.search);
      D.hookGlobalErrors();

      try {
        D.dbg("mount", { path: window.location.pathname, search: window.location.search });

        // セッションにプロファイルがあればそれを採用
        if (profile?.userId) {
          D.dbg("profile-cache-hit", { userId: profile.userId });
          return;
        }

        const search = new URLSearchParams(routerLocation.search);
        const errParam = search.get("error");
        if (errParam) {
          const desc = search.get("error_description") || errParam;
          D.dbg("auth-error-returned", { errParam, desc });
          setError(new Error(desc));
          return;
        }

        const code = search.get("code");
        const state = search.get("state");
        D.dbg("query-parsed", { hasCode: !!code, state, dbg: D.dbgEnabled() });

        // state に debug が含まれていれば有効化
        D.maybeEnableFromState(state);

        if (!code) {
          D.dbg("no-code", { note: "LINE未認証の直接遷移（通常）。必要なら ensureAuth で誘導。" });
          return;
        }

        // state 照合
        const savedState = sessionStorage.getItem(SS_KEY_STATE);
        const enforce = (import.meta?.env?.VITE_LINE_ENFORCE_STATE ?? process.env.REACT_APP_LINE_ENFORCE_STATE) === "true";
        if (enforce) {
          if (!savedState) throw new Error("Missing state");
          if (savedState !== state) throw new Error("Invalid state");
        } else if (savedState && savedState !== state) {
          throw new Error("Invalid state");
        }
        D.dbg("state-checked", { enforce, hasSavedState: !!savedState });

        // トークン交換
        const functions = getFunctions(app, "asia-northeast2");
        const exchange = httpsCallable(functions, "exchangeLineCode");
        D.dbg("exchange-call", { redirectUri });
        const res = await exchange({ code, state, redirectUri });

        const p = res?.data?.profile;
        const token = res?.data?.customToken;
        if (!p?.userId) throw new Error("Failed to get profile");
        if (!token) throw new Error("Failed to get custom token");

        // Firebase Auth にログイン（uid = LINE userId）
        await signInWithCustomToken(auth, token);

        sessionStorage.setItem(SS_KEY_PROFILE, JSON.stringify(p));
        setProfile(p);
        setLineUserId(p.userId);
        D.dbg("profile-obtained", { userId: p.userId, displayName: p.displayName });

        // URL クリーンアップ
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, "", cleanUrl);
        D.dbg("url-cleaned", { to: cleanUrl });
      } catch (e) {
        D.dbgError("useLineAuth-error", e);
        setError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.search]);

  const ensureAuth = () => {
    const clientId =
      import.meta?.env?.VITE_LINE_CHANNEL_ID ||
      process.env.REACT_APP_LINE_CHANNEL_ID ||
      "1654106088";
    const scope = "openid profile";
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem(SS_KEY_STATE, state);
    const authUrl =
      `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=${encodeURIComponent(scope)}`;
    D.dbg("ensureAuth-redirect", { to: authUrl, redirectUri });
    window.location.href = authUrl;
  };

  return { profile, lineUserId, error, ensureAuth };
}
